import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';

import {
  ORDER_STATUS,
  useOrderList,
  useOrderTableSettings,
} from '@frontend/api';
import { VStack } from '@frontend/legacy-components';

import { PageTitle } from 'components/PageTitle';
import { SearchAndFilters, TabsSkeleton } from 'components/listTablePage';

import { getGroupId, getUserId } from 'store/session/selectors';

import { toCapitalizedReadableString } from 'utils/stringUtils';

import {
  PageHeader,
  Filters,
  OrderListPageErrorContent,
  OrderListTabs,
} from './components';
import { useActiveTab, useOrderListSearchParams } from './hooks';

const OrdersListPageUnwrapped = () => {
  const userId = useSelector(getUserId);
  const groupId = useSelector(getGroupId);

  const activeTab = useActiveTab();

  const { data: orderTableSettings, isFetching: isFetchingSettings } =
    useOrderTableSettings({ userId });

  const { setSearch, searchParams, activeFilters, setFilters } =
    useOrderListSearchParams();
  const searchValue = searchParams.search ?? '';

  const orderListQuery = useOrderList(
    {
      locator: groupId,
      query: {
        ...searchParams,
        status: activeTab,
      },
    },
    {
      queryOptions: { keepPreviousData: true },
    },
  );

  const hasContentPrerequisites = userId && orderTableSettings;

  const contentIsShown = !isFetchingSettings && hasContentPrerequisites;

  return (
    <>
      <PageTitle title={`Orders (${toCapitalizedReadableString(activeTab)})`} />
      <PageHeader>
        {contentIsShown && (
          <SearchAndFilters
            inputPlaceholder="Search by order number, quote number, account details, and more"
            filtersContent={
              <Filters
                onApplyFilters={setFilters}
                activeFilters={activeFilters}
                isFetching={orderListQuery.isFetching}
              />
            }
            onSearch={setSearch}
            searchValue={searchValue}
          />
        )}
      </PageHeader>
      {isFetchingSettings ? (
        <TabsSkeleton tabIds={Object.values(ORDER_STATUS)} />
      ) : hasContentPrerequisites ? (
        <OrderListTabs
          userId={userId}
          orderListQuery={orderListQuery}
          orderTableSettings={orderTableSettings}
        />
      ) : (
        <OrderListPageErrorContent />
      )}
    </>
  );
};

export const OrdersListPage = () => {
  return (
    <VStack
      data-testid="order-list-page"
      spacing={2}
      justifyContent="flex-start"
      alignItems="stretch"
      height="100%"
      py={3}
      px={{ base: 2, tablet: 3 }}
    >
      <ErrorBoundary
        fallback={
          <>
            <PageHeader />
            <OrderListPageErrorContent />
          </>
        }
      >
        <OrdersListPageUnwrapped />
      </ErrorBoundary>
    </VStack>
  );
};
